import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import PortalContainer from "./PortalContainer";
import classNames from "classnames";
import { UXPContext } from '@core/context/Context';
import { CSSTransition } from "react-transition-group";
import IconButton from "@widget/buttons/IconButton";
// Default values for a few props
const defaultModalProps = {
    backgroundDismiss: true,
    showCloseButton: true,
    animation: "zoom",
};
/**
 * @export
 * Display a modal dialog. The dialog will be placed in front of a invisible sheet above the main UI.
 *
 * @example
 * ```
 *  <button
 *      className="btn showcase"
 *      onClick={() => setShowModal(true)}
 *  >
 *      Click to Show Modal
 *  </button>
 *
 *  <Modal
 *      show={showModal}
 *      onOpen={() => { }}
 *      onClose={() => setShowModal(false)}
 *  >
 *      This is a sample modal
 *  </Modal>
 * ```
 *
 */
const Modal = (props) => {
    // Merge default values with provided props
    const mergedProps = Object.assign(Object.assign({}, defaultModalProps), props);
    const context = React.useContext(UXPContext);
    const modalBackdropRef = React.useRef(null);
    const nodeRef = React.useRef(null);
    // Call onOpen or onClose when the 'show' prop changes.
    React.useEffect(() => {
        mergedProps.show ? onOpen() : onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergedProps.show]);
    // Close on Escape key while the modal is visible.
    React.useEffect(() => {
        if (!mergedProps.show)
            return;
        const handler = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergedProps.show]);
    const onOpen = () => {
        if (typeof mergedProps.onOpen === "function") {
            mergedProps.onOpen();
        }
    };
    const onClose = () => {
        if (typeof mergedProps.onClose === "function") {
            mergedProps.onClose();
        }
    };
    // Dismiss modal if clicking on backdrop
    const handleClick = (e) => {
        if (mergedProps.backgroundDismiss && e.target === modalBackdropRef.current) {
            onClose();
        }
    };
    const backdropStyles = mergedProps.backdropStyles ? Object.assign({}, mergedProps.backdropStyles) : {};
    const modalStyles = {};
    if (mergedProps.autoSize) {
        modalStyles.minWidth = "auto";
        modalStyles.minHeight = "auto";
    }
    return (_jsx(CSSTransition, { in: mergedProps.show, timeout: 300, classNames: mergedProps.animation, unmountOnExit: true, nodeRef: nodeRef, children: _jsx(PortalContainer, { children: _jsxs("div", { ref: nodeRef, className: classNames("uxpcore_modal", mergedProps.className), style: mergedProps.styles, children: [_jsx("div", { className: "uxpcore_modal__backdrop", ref: modalBackdropRef, onClick: handleClick, style: backdropStyles }), mergedProps.renderAdditionalContent && mergedProps.renderAdditionalContent(), _jsxs("div", { className: "uxpcore_modal__panel", style: modalStyles, children: [_jsxs("div", { className: "uxpcore_modal__header", children: [mergedProps.headerContent === undefined ? (_jsx("div", { className: "uxpcore_modal__title", children: mergedProps.title === undefined
                                            ? context.$L("uxp-core.text.modal")
                                            : mergedProps.title })) : (mergedProps.headerContent), mergedProps.showCloseButton && (_jsx("div", { className: "uxpcore_modal__close", children: mergedProps.closeButton !== undefined ? (_jsx("div", { onClick: onClose, children: mergedProps.closeButton })) : (_jsx(IconButton, { type: "close", variant: "secondary", size: "small", onClick: onClose })) }))] }), _jsx("div", { className: "uxpcore_modal__body", children: mergedProps.children })] })] }) }) }));
};
export default Modal;
